<script lang="ts" setup>
import {
  type Component,
  type PrimaryFeeling,
  type ThinkingTrap,
  ComponentType,
  QuestionnaireType,
} from '@/ts/types/dto/interventions-dto';
import ReadOnlyContent from '@/vue/molecules/read-only/read-only-content.vue';
import ReadOnlyJournal from '@/vue/molecules/read-only/read-only-journal.vue';
import ReadOnlyConceptualization from '@/vue/molecules/read-only/read-only-conceptualization.vue';
import ReadOnlyTable from '@/vue/molecules/read-only/read-only-table.vue';
import ReadOnlyQuestionnaire from '@/vue/molecules/read-only/questionnaire/read-only-questionnaire.vue';
import ReadOnlyProblemSolving from '@/vue/molecules/read-only/read-only-problem-solving.vue';
import ErrorBox from '@/vue/molecules/error-box.vue';
import ReadOnlyLayout from '@/vue/molecules/read-only/read-only-layout.vue';
import ImageGrid from '@/vue/molecules/image-grid.vue';
import {computed} from 'vue';

const props = defineProps<{
  component: Component;
  primaryFeelings: PrimaryFeeling[];
  thinkingTraps: ThinkingTrap[];
  pageComponents?: Component[];
}>();

// Read-only (review) path: derive the recorded weight from the WEIGHT_LOSS
// questionnaire on the same page, to feed the weight-loss % display.
// Passed down as a prop (not via inject) because the read-only tree renders
// outside session-cms-content — which is what provides CURRENT_SESSION_ID — so
// the session-scoped useSessionWeight composable can't resolve a session here.
const currentWeightGrams = computed<number | null>(() => {
  const components = props.pageComponents;
  if (!components) {
    return null;
  }
  const weightComponent = components.find(
    component => component.type === ComponentType.QUESTIONNAIRE
      && component.questionnaire?.type === QuestionnaireType.WEIGHT_LOSS,
  );
  const weightQuestion = weightComponent?.questionnaire?.questions.find(
    question => question.numeric && question.numericSelected !== null && question.numericSelected !== void 0,
  );
  return weightQuestion?.numericSelected ?? null;
});

</script>
<template>
  <read-only-content
    v-if="component.type === ComponentType.CONTENT"
    :content="component.content"
  />
  <read-only-journal
    v-else-if="component.type === ComponentType.JOURNAL"
    :journal="component.journal"
  />
  <read-only-conceptualization
    v-else-if="component.type === ComponentType.CONCEPTUALIZATION_WORKSHEET"
    :worksheet="component.conceptualizationWorksheet"
    :primary-feelings="primaryFeelings"
    :thinking-traps="thinkingTraps"
  />
  <read-only-table
    v-else-if="component.type === ComponentType.TABLE"
    :table="component.table"
  />
  <read-only-questionnaire
    v-else-if="component.type === ComponentType.QUESTIONNAIRE"
    :questionnaire="component.questionnaire"
    :current-weight-grams="currentWeightGrams"
  />
  <!-- A review arrives with no content whenever the CMS has none linked for the outcome it
       reaches. It draws nothing at all then, which is why the error box below skips its type: the
       two conditions are one rule and have to be read together. -->
  <read-only-content
    v-else-if="component.type === ComponentType.QUESTIONNAIRE_REVIEW && component.questionnaireReview?.content"
    :content="component.questionnaireReview.content"
  />
  <read-only-problem-solving
    v-else-if="component.type === ComponentType.PROBLEM_SOLVING_WORKSHEET"
    :worksheet="component.problemSolvingWorksheet"
  />
  <read-only-layout
    v-else-if="component.type === ComponentType.LAYOUT"
    :layout="component.componentLayout"
  />
  <image-grid
    v-else-if="component.type === ComponentType.IMAGE_GRID"
    :image-grid="component.imageGrid"
  />
  <error-box v-else-if="component.type !== ComponentType.QUESTIONNAIRE_REVIEW"/>
</template>
